/**
 * Fetches one property-map tile (M1-26), authenticated. A Leaflet
 * `TileLayer`'s `<img src>` cannot carry an `Authorization` header, so
 * `PropertyLocationMap` uses a custom `GridLayer` whose `createTile` calls
 * this instead of letting Leaflet set `img.src` directly — see
 * web/CONVENTIONS.md's M1-26 section for why.
 *
 * Goes through `apiClient`, NOT a hand-rolled `fetch`: the base-URL rule and
 * the bearer-token middleware are decided in exactly one place, so anything
 * added to that chain later (a correlation header, 401 refresh, telemetry)
 * reaches tiles too. `parseAs: 'blob'` is what makes the typed client hand
 * back bytes instead of trying to parse JSON.
 */
import { apiClient } from '@/api/client';

/**
 * The deepest zoom the tile proxy serves — must match `MapsEndpoints.MaxZoom`,
 * which 400s anything past it. Leaflet has no bound of its own here (a
 * `GridLayer` subclass, unlike `TileLayer`, defaults `maxZoom` to `undefined`,
 * which makes `map.getMaxZoom()` `Infinity`), so the map has to be told: without
 * it the zoom-in control never disables and zooming past this turns every tile
 * into a 400 and the map into a silent grey square.
 */
export const MAX_TILE_ZOOM = 19;

export class MapTileError extends Error {
  constructor(readonly status: number) {
    super(`Map tile fetch failed: ${status}`);
    this.name = 'MapTileError';
  }
}

export async function fetchMapTile(
  zoom: number,
  x: number,
  y: number,
  signal?: AbortSignal,
): Promise<Blob> {
  const { data, response } = await apiClient.GET('/api/v1/maps/tiles/{z}/{x}/{y}', {
    params: { path: { z: zoom, x, y } },
    parseAs: 'blob',
    signal,
  });

  // openapi-fetch returns a non-2xx rather than throwing, and the caller
  // (`AuthenticatedTileLayer.createTile`) needs a rejection to hand Leaflet's
  // `done(err)` — which is what drives the "map unavailable" fallback.
  if (!response.ok || !data) {
    throw new MapTileError(response.status);
  }

  return data;
}
